import { useAuth } from "@clerk/clerk-react";
import Masonry from "@mui/lab/Masonry";
import { Box, Grid } from "@mui/material";
import { ActionItemsContext } from "contexts/ActionItems";
import { AlertContext } from "contexts/Alert";
import { GamificationContext } from "contexts/Gamification";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  BrandDealJourney,
  SearchParams,
  UpgradeDialogSource,
  VaOrganizationProfileStatus,
} from "schemas/dashboard";
import {
  OrganizationAutopilotProfileStatus,
  OrganizationProProfileStatus,
} from "schemas/dashboard";
import { routes } from "schemas/routes";

import { PAGE_VISITED } from "constants/trackingProps";
import AutopilotOnboardingProgress from "features/Auth/Onboarding/AutopilotOnboarding/Progress";
import ProOnboardingProgress from "features/Auth/Onboarding/ProOnboarding/Progress";
import { useAccountReviewState } from "features/Auth/Onboarding/shared/AccountReviewUpsell";
import { fetcherAuth } from "utils/api";
import {
  DISPLAYED_INSTAGRAM_ONBOARDING_PAYWALL,
  DISPLAYED_ONBOARDING_PAYWALL,
  HIDE_ONBOARDING_STEPS,
} from "utils/localStorage";
import { getParams, trackEvent } from "utils/tracking";
import { useGamification } from "utils/useGamification";

import VaOnboardingProgress from "../VA/OnboardingProgress";
import ActionItemList from "./ActionItemList";
import PremiumFeatureUnlocked from "./Gamification/PremiumFeatureUnlocked";
import CreateNicheTemplateCard from "./Gamification/components/CreateNicheTemplateCard";
import ExperienceFreeLevelOne from "./Gamification/components/ExperienceFreeLevelOne";
import ExperiencePro from "./Gamification/components/ExperiencePro";
import ExperienceVa from "./Gamification/components/ExperienceVa";
import WeeklyConsistencyChallenge from "./Gamification/components/WeeklyConsistencyChallenge";
import { isTaskCompleted } from "./Gamification/helpers";
import { OrganizationGamificationTask } from "./Gamification/schema";
import OnboardingStepper, {
  getVisibleOnboardingSteps,
} from "./OnboardingStepper";
import RequestOnboardingReview from "./RequestOnboardingReview";
import { useOnboardingReviewCard } from "./RequestOnboardingReview/useOnboardingReviewCard";
import ResourcesSection from "./ResourcesSection";
import SendPitch from "./SendPitch";
import SpecialAnnouncementCard from "./SpecialAnnouncement";

const HomePage = () => {
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const {
    currentOrg,
    organizationVaProfile,
    organizationProProfile,
    organizationAutopilotProfile,
    totalEmailsSent,
    setTotalEmailsSent,
    showGamificationProfile,
    gamificationProfile,
    profile,
  } = useContext(OrganizationUserContext);
  const { outreachTemplates, followupTemplates } = useContext(
    OutreachTemplatesContext,
  );
  const { actionItems, setActionItems } = useContext(ActionItemsContext);
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);
  const { pitchCount, setPitchCount } = useContext(GamificationContext);
  const { canSubmitReview } = useAccountReviewState();
  const reviewCard = useOnboardingReviewCard();
  const { fetchGamificationProfile } = useGamification();

  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchPitchCountLoading, setFetchPitchCountLoading] = useState(false);
  const [showPremiumFeatureUnlocked, setShowPremiumFeatureUnlocked] =
    useState(false);

  const isFromInstagramOnboarding =
    localStorage.getItem(
      `${DISPLAYED_INSTAGRAM_ONBOARDING_PAYWALL}-${currentOrg?.id}`,
    ) === "true";
  const alreadyDisplayedOnboardingPaywall =
    localStorage.getItem(
      `${DISPLAYED_ONBOARDING_PAYWALL}-${currentOrg?.id}`,
    ) === "true";

  const upgradeDialogSource = getParams(SearchParams.UPGRADE_DIALOG_SOURCE);

  const fetchActionItems = async () => {
    setFetchLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/action-items`,
      );
      setActionItems(res.actionItems);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const checkIfEmailsSent = async () => {
    setFetchLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/emails/total-count`,
      );
      setTotalEmailsSent(res.count);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchOutreachCount = async () => {
    if (!currentOrg) return;
    setFetchPitchCountLoading(true);
    try {
      const { count } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/gamification/total-outreach-count`,
      );
      setPitchCount(count);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchPitchCountLoading(false);
    }
  };

  const displayPaywallsPostOnboarding = () => {
    if (currentOrg?.isBrand) {
      return;
    }
    if (
      upgradeDialogSource &&
      Object.values(UpgradeDialogSource).includes(
        upgradeDialogSource as UpgradeDialogSource,
      )
    ) {
      setUpgradeDialogSource(upgradeDialogSource as UpgradeDialogSource);
      return;
    }
    const hasSubscribed = subscription?.isSubscribed;
    // Display usual onboarding paywall
    if (hasSubscribed) {
      return;
    }

    if (!alreadyDisplayedOnboardingPaywall) {
      setUpgradeDialogSource(UpgradeDialogSource.onboarding_paywall);
    }
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchActionItems();
      checkIfEmailsSent();
      fetchOutreachCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    if (currentOrg?.id) {
      displayPaywallsPostOnboarding();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentOrg?.id,
    alreadyDisplayedOnboardingPaywall,
    isFromInstagramOnboarding,
  ]);

  useEffect(() => {
    if (currentOrg?.id && showGamificationProfile && !gamificationProfile) {
      fetchGamificationProfile(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGamificationProfile, currentOrg?.id]);

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (currentOrg?.isBrand) {
    return <Navigate to={`/${routes.inbox}`} replace />;
  }

  const onboardingStepsHidden =
    localStorage.getItem(`${HIDE_ONBOARDING_STEPS}-${currentOrg?.id}`) ===
    "true";

  const renderLeftHandColumn = () => {
    if (!currentOrg?.isPaidUser && showGamificationProfile) {
      if (
        profile?.brandDealJourney === BrandDealJourney.work_regularly &&
        !onboardingStepsHidden
      ) {
        const hasVisibleSteps =
          getVisibleOnboardingSteps({
            profile,
            outreachTemplates,
            followupTemplates,
            totalEmailsSent,
          }).length > 0;
        return hasVisibleSteps ? (
          <OnboardingStepper totalEmailsSent={totalEmailsSent} />
        ) : null;
      } else if (
        profile?.brandDealJourney !== BrandDealJourney.work_regularly
      ) {
        return (
          <ExperienceFreeLevelOne
            pitchCount={pitchCount}
            onRefreshPitchCount={fetchOutreachCount}
            fetchPitchCountLoading={fetchPitchCountLoading}
            setShowPremiumFeatureUnlocked={setShowPremiumFeatureUnlocked}
          />
        );
      } else {
        return null;
      }
    }

    if (
      currentOrg?.isProUser &&
      organizationProProfile?.status === OrganizationProProfileStatus.later
    ) {
      return <ProOnboardingProgress />;
    } else if (
      currentOrg?.isAutopilotUser &&
      organizationAutopilotProfile?.status ===
        OrganizationAutopilotProfileStatus.later
    ) {
      return <AutopilotOnboardingProgress />;
    } else if (
      currentOrg?.isVaUser &&
      organizationVaProfile?.status &&
      ![
        VaOrganizationProfileStatus.completed,
        VaOrganizationProfileStatus.later,
      ]?.includes(organizationVaProfile?.status!)
    ) {
      return <VaOnboardingProgress />;
    }

    if (canSubmitReview && reviewCard.visible) {
      return <RequestOnboardingReview card={reviewCard} />;
    }
    return null;
  };

  // At most two cards, by priority: onboarding/review → action items → pitch.
  const hasPrimaryCard = renderLeftHandColumn() !== null;
  const hasActionItems = fetchLoading || (actionItems?.length ?? 0) > 0;
  const showPitch =
    Boolean(currentOrg?.isPaidUser) ||
    Boolean(currentOrg?.gamificationStatus?.levelOneComplete);
  const shownCards = [
    ...(hasPrimaryCard ? (["primary"] as const) : []),
    ...(hasActionItems ? (["actionItems"] as const) : []),
    ...(showPitch ? (["pitch"] as const) : []),
  ].slice(0, 2);

  const topCards = [
    ...shownCards.map((card) => (
      <Box key={card}>
        {card === "primary" && renderLeftHandColumn()}
        {card === "actionItems" && (
          <ActionItemList fetchLoading={fetchLoading} />
        )}
        {card === "pitch" && <SendPitch />}
      </Box>
    )),
  ];

  return (
    <Grid sx={{ pb: { xs: 10, md: 0 } }}>
      <Grid item xs={12}>
        <SpecialAnnouncementCard />
      </Grid>

      <Masonry
        columns={{ xs: 1, lg: topCards.length > 1 ? 2 : 1 }}
        spacing={1}
        sx={{ width: "auto", m: 0 }}
      >
        {topCards}
      </Masonry>

      {showGamificationProfile && currentOrg?.isProUser && <ExperiencePro />}
      {showGamificationProfile && currentOrg?.isVaUser && <ExperienceVa />}

      {showGamificationProfile &&
        !currentOrg?.isPaidUser &&
        gamificationProfile?.unlockPremiumFeatures && (
          <>
            <Box sx={{ mt: 1 }}>
              <CreateNicheTemplateCard
                title={"Create Niche-Specific Template"}
                description="Customize your pitch for a specific niche (e.g., beauty, fitness, tech)"
                completed={isTaskCompleted(
                  gamificationProfile,
                  OrganizationGamificationTask.niche_templates,
                  1,
                )}
                infoBox={
                  <>
                    {" "}
                    <strong>Pro tip:</strong> Different niches respond to
                    different pitching styles. A beauty brand cares about
                    aesthetics and engagement, while a tech brand wants to see
                    expertise and audience demographics.
                  </>
                }
              />
            </Box>

            <Box sx={{ mt: 1 }}>
              <WeeklyConsistencyChallenge
                completed={isTaskCompleted(
                  gamificationProfile,
                  OrganizationGamificationTask.email_streak,
                  1,
                )}
              />
            </Box>
          </>
        )}

      <ResourcesSection />

      {showPremiumFeatureUnlocked && (
        <PremiumFeatureUnlocked
          isOpen={showPremiumFeatureUnlocked}
          onClose={() => setShowPremiumFeatureUnlocked(false)}
          onUpgrade={() =>
            setUpgradeDialogSource(
              UpgradeDialogSource.gamification_premium_unlocked,
            )
          }
        />
      )}
    </Grid>
  );
};

export default HomePage;
